import type { LoginDefault } from '@up2d8/contracts';

/**
 * Waar het aanmeldscherm van de app begint: e-mail of de pincode (ORG-3, ADR-0099).
 *
 * Vóór het aanmelden kent de app de organisatie niet uit een sessie. Ze kent ze wel op drie
 * betrouwbare manieren, zonder iets nieuws te vragen:
 *
 * 1. **`?org=<code>` in de link** (een QR op een affiche, een link in een mail van de club). Dan wint
 *    de aanmeldwijze van die organisatie: wie zo'n link volgt, hoort bij haar.
 * 2. **Hoe dit toestel zich de vorige keer aanmeldde.** De sterkste aanwijzing: in een fabriek meldt
 *    de operator zich aan met de pincode, de bediende met e-mail - op hun eigen telefoon.
 * 3. **De bedrijfscode die dit toestel onthoudt** (na een aanmelding met pincode, een activatie of een
 *    registratie): dan de standaard van haar type, zoals Master die zet.
 *
 * Geen van drie: e-mail, de weg van de uitnodigingsmail (LOG-1). De link naar de andere wijze staat
 * altijd onder het formulier; dit kiest alleen waar het scherm begint.
 *
 * Bewust niet: de laatst gebruikte organisatie uit een vorige sessie halen, of het platformmerk. Een
 * sessie kan verlopen of van iemand anders zijn, en het platformmerk zegt niets over één klant.
 */
export type LoginMode = LoginDefault;

const MODE_KEY = 'up2d8.loginMode';

export function readRememberedLoginMode(): LoginMode | null {
  try {
    const value = window.localStorage.getItem(MODE_KEY);
    return value === 'email' || value === 'pin' ? value : null;
  } catch {
    // Privémodus of geblokkeerde opslag: dan beslist de organisatie, of e-mail.
    return null;
  }
}

/** Na een geslaagde aanmelding, activatie of registratie: zo begint het scherm de volgende keer. */
export function rememberLoginMode(mode: LoginMode): void {
  try {
    window.localStorage.setItem(MODE_KEY, mode);
  } catch {
    // Niets aan te doen, en niets om de aanmelding voor te laten mislukken.
  }
}

/** De keuze zelf, zonder opslag of netwerk: zie de volgorde hierboven. */
export function chooseLoginMode(input: {
  /** De aanmeldwijze van de organisatie uit `?org=`, als die gevonden werd. */
  linked?: LoginDefault | null;
  remembered?: LoginMode | null;
  /** De aanmeldwijze van de onthouden organisatie, als die gevonden werd. */
  rememberedTenant?: LoginDefault | null;
}): LoginMode {
  return input.linked ?? input.remembered ?? input.rememberedTenant ?? 'email';
}

/** De organisatiecode uit de link (`?org=`), of `null`. */
export function linkedTenantCode(search: string): string | null {
  const code = new URLSearchParams(search).get('org')?.trim().toLowerCase() ?? '';
  return /^[a-z0-9][a-z0-9-]{1,10}[a-z0-9]$/.test(code) ? code : null;
}
